<!--
  @fileoverview Grid Message Component - Image Grid Renderer

  Renders images in a responsive grid layout within chat messages.
  Grid columns are configurable via message options.

  @component MessageGrid
  @displayName Image Grid

  Features:
  - Configurable column count
  - Click to open lightbox
  - Responsive flex layout
  - Background image styling for consistent sizing

  @example
  <MessageGrid
    :message="gridMessage"
    :button-outline="'primary'"
    :should-disable-if-in-livechat="false"
    @setLightBoxImage="openLightbox"
  />

  @vue3migration
  - Converted to script setup pattern
  - Removed unused Vue and lodash-es imports
-->
<template>
  <div class="keyreply-message-grid">
    <div
      :class="['keyreply-grid-item', gridItemClass]"
      v-for="(item, index) in message.data.content[0].images"
      :key="index"
    >
      <div class="keyreply-grid-image" @click="$emit('setLightBoxImage', item)">
        <img :src="item" class="w-full h-full object-cover rounded-[16px]" />
      </div>
    </div>
  </div>
</template>

<script setup>
/**
 * @module components/ChatWindow/Message/Grid
 * @description Image grid layout renderer for chat messages
 */
import { computed } from "vue";

/**
 * Component props
 */
const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
  buttonOutline: {
    type: String,
    default: "",
  },
  shouldDisableIfInLivechat: {
    type: Boolean,
    default: false,
  },
});

/**
 * Component emits
 */
defineEmits(["setLightBoxImage"]);

/**
 * Map column count to CSS class for grid item flex-basis.
 * Uses predefined classes to avoid inline styles (CSP compliant).
 *
 * Supported column counts: 1-6
 * Falls back to 50% (2 columns) for unsupported values.
 */
const gridItemClass = computed(() => {
  const columnCount = props.message.data.content[0].options?.column;
  const classMap = {
    1: "grid-cols-1",
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-4",
    5: "grid-cols-5",
    6: "grid-cols-6",
  };
  return classMap[columnCount] || "grid-cols-2";
});
</script>

<style lang="scss" scoped>
.keyreply-message-grid {
  display: flex;
  flex-wrap: wrap;
  margin: 16px 8px;

  .keyreply-grid-item {
    padding: 8px;
    flex-basis: 50%;
    box-sizing: border-box;

    /* CSP-compliant flex-basis classes for different column counts */
    &.grid-cols-1 {
      flex-basis: 100%;
    }
    &.grid-cols-2 {
      flex-basis: 50%;
    }
    &.grid-cols-3 {
      flex-basis: 33.333%;
    }
    &.grid-cols-4 {
      flex-basis: 25%;
    }
    &.grid-cols-5 {
      flex-basis: 20%;
    }
    &.grid-cols-6 {
      flex-basis: 16.666%;
    }
  }

  .keyreply-grid-image {
    width: 100%;
    height: 0;
    padding-bottom: 100%;
    border-radius: 16px;
    cursor: pointer;
    position: relative;

    img {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 16px;
    }
  }
}
</style>
